import { useEffect, useMemo } from 'react'
import { ModalForm } from '@ant-design/pro-components'
import { useMutation } from '@tanstack/react-query'
import { App, Form, Input, InputNumber, Radio, Tag } from 'antd'
import {
  BUSINESS_DEFAULT_TAG_COLOR,
  OPTIONAL_RENTAL_FEE_NOTICE,
  type BusinessUpsertInput
} from '@super-mega/server/contracts/business'
import { useTRPC } from '@/trpc/react.ts'
import { getErrorMessage } from '@/utils/error.ts'
import { BusinessTagColorPicker } from './business-tag-color-picker.tsx'

export type BusinessEditorRecord = {
  defaultServiceMode?: 'once' | 'term'
  id: number
  name: string
  type: 'lease' | 'valueAdded'
  color: string
  referencePrice: string | null
  costPrice: string | null
  waterUnitPrice?: string | null
  electricityUnitPrice?: string | null
  depositAmount?: string | null
  managementFee?: string | null
  note: string | null
}

export type BusinessFormValues = BusinessUpsertInput

export function BusinessEditorModal({
  editing,
  fixedType,
  onClose,
  onSaved
}: {
  editing?: BusinessEditorRecord | 'new'
  fixedType?: BusinessFormValues['type']
  onClose: () => void
  onSaved: (business: BusinessEditorRecord) => Promise<void>
}) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [form] = Form.useForm<BusinessFormValues>()
  const watchedType = Form.useWatch('type', form) as BusinessFormValues['type'] | undefined
  const businessType = watchedType ?? fixedType ?? 'lease'
  const businessName = Form.useWatch('name', form) as string | undefined
  const businessColor = (Form.useWatch('color', form) as string | undefined) ?? BUSINESS_DEFAULT_TAG_COLOR
  const upsert = useMutation(trpc.business.upsert.mutationOptions())
  const initialValues = useMemo<Partial<BusinessFormValues>>(
    () =>
      editing && editing !== 'new'
        ? {
            costPrice: editing.costPrice === null ? undefined : Number(editing.costPrice),
            color: editing.color || BUSINESS_DEFAULT_TAG_COLOR,
            name: editing.name,
            note: editing.note ?? undefined,
            referencePrice: editing.referencePrice === null ? undefined : Number(editing.referencePrice),
            waterUnitPrice: editing.waterUnitPrice == null ? undefined : Number(editing.waterUnitPrice),
            electricityUnitPrice:
              editing.electricityUnitPrice == null ? undefined : Number(editing.electricityUnitPrice),
            depositAmount: editing.depositAmount == null ? undefined : Number(editing.depositAmount),
            managementFee: editing.managementFee == null ? undefined : Number(editing.managementFee),
            defaultServiceMode: editing.defaultServiceMode ?? 'once',
            type: editing.type
          }
        : {
            defaultServiceMode: 'once',
            color: BUSINESS_DEFAULT_TAG_COLOR,
            name: '',
            note: '',
            type: fixedType ?? 'lease'
          },
    [editing, fixedType]
  )

  useEffect(() => {
    if (!editing) {
      return
    }
    form.resetFields()
    form.setFieldsValue(initialValues)
  }, [editing, form, initialValues])

  const save = async (values: BusinessFormValues) => {
    try {
      const business = await upsert.mutateAsync({
        id: editing === 'new' ? undefined : editing?.id,
        data: {
          costPrice: values.costPrice ?? null,
          color: values.color,
          name: values.name.trim(),
          note: values.note?.trim() || null,
          referencePrice: values.referencePrice ?? null,
          waterUnitPrice: (fixedType ?? values.type) === 'lease' ? (values.waterUnitPrice ?? null) : null,
          electricityUnitPrice: (fixedType ?? values.type) === 'lease' ? (values.electricityUnitPrice ?? null) : null,
          depositAmount: (fixedType ?? values.type) === 'lease' ? (values.depositAmount ?? null) : null,
          managementFee: (fixedType ?? values.type) === 'lease' ? (values.managementFee ?? null) : null,
          defaultServiceMode: values.defaultServiceMode,
          type: fixedType ?? values.type
        }
      })
      void message.success(editing === 'new' ? '业务已创建' : '业务已更新')
      await onSaved(business)
      return true
    } catch (error) {
      void message.error(getErrorMessage(error, '业务保存失败'))
      return false
    }
  }

  return (
    <ModalForm<BusinessFormValues>
      form={form}
      name="business-editor"
      initialValues={initialValues}
      layout="vertical"
      modalProps={{ destroyOnHidden: true, mask: { closable: false }, onCancel: onClose }}
      onFinish={save}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
      open={Boolean(editing)}
      scrollToFirstError
      submitter={{
        resetButtonProps: { onClick: onClose },
        searchConfig: { resetText: '取消', submitText: '保存业务' },
        submitButtonProps: { loading: upsert.isPending }
      }}
      title={editing === 'new' ? '新建业务' : `编辑业务 · ${editing?.name ?? ''}`}
      width="40rem"
    >
      <Form.Item label="业务名称" name="name" rules={[{ max: 255, required: true }]}>
        <Input placeholder="请输入业务名称，如：设备租赁" />
      </Form.Item>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Form.Item label="业务类型" name="type" rules={[{ message: '请选择业务类型', required: true }]}>
          <Radio.Group
            disabled={fixedType !== undefined}
            optionType="button"
            options={[
              { label: '租赁业务', value: 'lease' },
              { label: '增值业务', value: 'valueAdded' }
            ]}
          />
        </Form.Item>
        {businessType === 'valueAdded' && (
          <Form.Item label="服务模式" name="defaultServiceMode">
            <Radio.Group
              optionType="button"
              options={[
                { label: '一次性服务', value: 'once' },
                { label: '期限服务', value: 'term' }
              ]}
            />
          </Form.Item>
        )}
      </div>
      <Form.Item
        extra={
          <span className="mt-2 flex flex-wrap items-center gap-2 px-1">
            <span>用于列表中快速识别，可选择预设颜色或自定义颜色。</span>
            <span aria-hidden>当前预览：</span>
            <Tag color={businessColor}>{businessName?.trim() || '业务名称'}</Tag>
          </span>
        }
        label="标签颜色"
        name="color"
        rules={[{ message: '请选择标签颜色', required: true }]}
      >
        <BusinessTagColorPicker />
      </Form.Item>
      <div className="grid gap-x-4 sm:grid-cols-2">
        <Form.Item extra="价格仅作参考。" label="参考价格" name="referencePrice">
          <InputNumber className="w-full!" max={9_999_999_999.99} min={0} placeholder="选填" precision={2} prefix="¥" />
        </Form.Item>
        <Form.Item extra="用于内部核算参考，可留空。" label="成本价格" name="costPrice">
          <InputNumber className="w-full!" max={9_999_999_999.99} min={0} placeholder="选填" precision={2} prefix="¥" />
        </Form.Item>
      </div>

      {businessType === 'lease' && (
        <>
          <p className="text-secondary mb-4">选择此业务开单时自动带入，均为选填。{OPTIONAL_RENTAL_FEE_NOTICE}</p>
          <div className="grid gap-x-4 sm:grid-cols-2">
            <Form.Item label="水费（元/吨）" name="waterUnitPrice" preserve={false}>
              <InputNumber
                className="w-full!"
                max={9_999_999_999.99}
                min={0}
                placeholder="留空不出账单"
                precision={2}
                prefix="¥"
              />
            </Form.Item>
            <Form.Item label="电费（元/度）" name="electricityUnitPrice" preserve={false}>
              <InputNumber
                className="w-full!"
                max={9_999_999_999.99}
                min={0}
                placeholder="留空不出账单"
                precision={2}
                prefix="¥"
              />
            </Form.Item>
            <Form.Item
              extra="开单时需确认押金，免押金请填写 0。"
              label="押金（元）"
              name="depositAmount"
              preserve={false}
            >
              <InputNumber
                className="w-full!"
                max={9_999_999_999.99}
                min={0}
                placeholder="选填"
                precision={2}
                prefix="¥"
              />
            </Form.Item>
            <Form.Item
              extra="按订单租金的计价单位及出账周期收取。"
              label="管理费（元/计价单位）"
              name="managementFee"
              preserve={false}
            >
              <InputNumber
                className="w-full!"
                max={9_999_999_999.99}
                min={0}
                placeholder="留空不出账单"
                precision={2}
                prefix="¥"
              />
            </Form.Item>
          </div>
        </>
      )}

      <Form.Item label="备注" name="note">
        <Input.TextArea maxLength={2000} placeholder="补充业务内容、计费方式等信息" rows={4} showCount />
      </Form.Item>
    </ModalForm>
  )
}
